import React from 'react';
import { 
  Box, Card, CardContent, Typography, useTheme
} from '@mui/material';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend
} from 'recharts';
import { getCountryName } from '../../../utils/countryUtils';

// Цвета для визуализации
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

// Компонент для отображения графика посещений по странам
const VisitsByCountryChart = ({ data }) => {
  const theme = useTheme();
  
  if (!data || data.length === 0) {
    return (
      <Card sx={{ 
        boxShadow: { xs: 1, sm: 2 },
        borderRadius: { xs: 1, sm: 2 }
      }}>
        <CardContent sx={{ textAlign: 'center', py: { xs: 2, sm: 3 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Нет данных о посещениях по странам
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Форматируем данные для графика
  const chartData = data.map(item => {
    // Преобразуем код страны в полное название
    const countryName = getCountryName(item.country);
    
    // Убедимся, что count преобразуется в число
    const visitCount = parseInt(item.count, 10) || 0;
    
    return {
      code: item.country || 'Неизвестно',
      name: countryName || 'Неизвестно',
      value: visitCount
    };
  });
  
  // Отладочный вывод для проверки данных
  console.log('Исходные данные по странам:', data);
  console.log('Преобразованные данные по странам:', chartData);

  // Используем хук для определения размера экрана
  const [windowSize, setWindowSize] = React.useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  
  React.useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Адаптивные размеры для круговой диаграммы
  const useSmallScreen = windowSize.width < 600;
  const outerRadius = useSmallScreen ? 60 : 80;

  // Рассчитываем общее количество посещений для процентов
  const totalVisits = chartData.reduce((sum, item) => sum + item.value, 0);
  
  // Отладка данных
  console.log('chartData в VisitsByCountryChart:', chartData);
  console.log('totalVisits в VisitsByCountryChart:', totalVisits);

  // Добавляем процентное соотношение к названию страны для легенды
  const enhancedChartData = chartData.map(item => {
    // Проверяем, что totalVisits не равен 0, чтобы избежать деления на ноль
    const percentage = totalVisits > 0 ? ((item.value / totalVisits) * 100).toFixed(1) : '0.0';
    console.log(`Страна: ${item.name}, Значение: ${item.value}, Процент: ${percentage}%`);
    
    return {
      ...item,
      percentage: percentage,
      displayName: `${item.name}: ${percentage}%`
    };
  });

  return (
    <Card sx={{ 
      boxShadow: { xs: 1, sm: 2 },
      borderRadius: { xs: 1, sm: 2 }
    }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Typography variant="h6" gutterBottom sx={{ 
          fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          Посещения по странам
        </Typography>
        <Box sx={{ height: { xs: 250, sm: 300, md: 350 }, mt: { xs: 1, sm: 2 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={enhancedChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={outerRadius}
                fill={theme.palette.primary.main}
                dataKey="value"
                nameKey="name"
                label={null} // Убираем подписи у самой диаграммы
              >
                {enhancedChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => {
                  // Используем уже рассчитанный процент из данных
                  const percentage = props.payload && props.payload.percentage ? props.payload.percentage : '0.0';
                  return [`${value} посещений (${percentage}%)`, props.payload.name];
                }}
                contentStyle={{ 
                  fontSize: '0.75rem',
                  padding: '8px',
                  borderRadius: '4px'
                }}
              />
              <Legend 
                formatter={(value, entry) => {
                  return entry.payload.displayName;
                }}
                layout={useSmallScreen ? "horizontal" : "vertical"}
                verticalAlign={useSmallScreen ? "bottom" : "middle"}
                align={useSmallScreen ? "center" : "right"}
                wrapperStyle={{ 
                  fontSize: useSmallScreen ? '0.65rem' : '0.75rem',
                  paddingLeft: useSmallScreen ? 0 : 10,
                  maxWidth: useSmallScreen ? '100%' : '40%',
                  overflowY: 'auto'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default VisitsByCountryChart;
